import React, { useState, useRef, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Star, Upload, Check, ChevronRight, Clock, Shield, 
  Phone, Mail, MapPin, Car, FileText, Wrench, 
  ClipboardCheck, Camera, CreditCard
} from "lucide-react";
import { Progress } from "./components/ui/progress";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Checkbox } from "./components/ui/checkbox";
import { Textarea } from "./components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CONTACT_EMAIL = "quentin@sasebv.com";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_exotic-appraisal/artifacts/aqjwn3v0_LOGO%20EBV%202025%20Blanc.png";

// Hero car image from design guidelines
const HERO_CAR_IMAGE = "https://static.prod-images.emergentagent.com/jobs/8a90a5bd-729d-47e7-8dad-f5e9533f5b4c/images/16db47fd6b82d94f088dbab8e96ad3468031009661469dec4cdee7d787f7d622.png";
const WHEEL_IMAGE = "https://static.prod-images.emergentagent.com/jobs/8a90a5bd-729d-47e7-8dad-f5e9533f5b4c/images/b62efe8a311108f0a90b0571f364f95889dd442df31a82b20c0c706eb213bb78.png";

const sections = [
  { id: 1, title: "Informations Client", icon: Phone },
  { id: 2, title: "Votre Demande", icon: FileText },
  { id: 3, title: "Informations Véhicule", icon: Car },
  { id: 4, title: "Photos Véhicule", icon: Camera },
  { id: 5, title: "Carte Grise", icon: CreditCard },
  { id: 6, title: "Frais de remise en état", icon: Wrench },
  { id: 7, title: "Défauts de carrosserie", icon: ClipboardCheck },
  { id: 8, title: "Entretien", icon: Wrench },
  { id: 9, title: "Déclaration", icon: Check },
];

// Star Rating Component
const StarRating = ({ value, onChange, label }) => {
  const [hover, setHover] = useState(0);
  
  return (
    <div className="space-y-2">
      <Label className="text-gray-700 text-sm">{label}</Label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            className="p-1 transition-transform hover:scale-110"
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            onClick={() => onChange(star)}
          >
            <Star
              className={`w-6 h-6 transition-colors ${
                star <= (hover || value)
                  ? "fill-yellow-500 text-yellow-500"
                  : "text-gray-300"
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

// File Upload Component
const FileUpload = ({ label, required, multiple, accept, files, onFilesChange, testId }) => {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragging(true);
    } else if (e.type === "dragleave") {
      setIsDragging(false);
    }
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("category", testId);
    
    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      return response.data;
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(`Erreur lors de l'upload de ${file.name}`);
      return null;
    }
  };

  const handleFiles = async (fileList) => {
    setUploading(true);
    const newFiles = Array.from(fileList);
    const uploadedFiles = [];
    
    for (const file of newFiles) {
      const result = await uploadFile(file);
      if (result) {
        uploadedFiles.push({
          id: result.id,
          path: result.path,
          name: file.name,
          size: result.size
        });
      }
    }
    
    if (multiple) {
      onFilesChange([...files, ...uploadedFiles]);
    } else {
      onFilesChange(uploadedFiles.slice(0, 1));
    }
    setUploading(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await handleFiles(e.dataTransfer.files);
    }
  };

  const handleChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      await handleFiles(e.target.files);
    }
  };

  const removeFile = (index) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    onFilesChange(newFiles);
  };

  return (
    <div className="space-y-2">
      <Label className="text-gray-700 text-sm">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      <div
        data-testid={testId}
        className={`dropzone rounded-sm p-6 text-center cursor-pointer ${isDragging ? "active" : ""}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={accept}
          multiple={multiple}
          onChange={handleChange}
        />
        <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
        <p className="text-gray-600 text-sm">
          {uploading ? "Upload en cours..." : "Glissez vos fichiers ou cliquez pour sélectionner"}
        </p>
        <p className="text-gray-400 text-xs mt-1">
          {accept?.replace(/\./g, "").replace(/,/g, ", ").toUpperCase()}
        </p>
      </div>
      {files.length > 0 && (
        <div className="space-y-2 mt-2">
          {files.map((file, index) => (
            <div key={index} className="flex items-center justify-between bg-gray-100 rounded-sm px-3 py-2">
              <span className="text-gray-700 text-sm truncate flex-1">{file.name}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                className="text-gray-400 hover:text-gray-700 ml-2"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

function App() {
  const [currentSection, setCurrentSection] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const sectionRefs = useRef({});

  // Form state
  const [formData, setFormData] = useState({
    // Section 1: Client Info
    nom: "",
    prenom: "",
    telephone: "",
    email: "",
    codePostal: "",
    // Section 2: Request Type
    typeDemande: "",
    // Section 3: Vehicle Info
    marque: "",
    modele: "",
    jourCirculation: "",
    moisCirculation: "",
    anneeCirculation: "",
    immatriculation: "",
    kilometrage: "",
    finition: "",
    paysOrigine: "",
    transmission: "",
    energie: "",
    puissance: "",
    couleur: "",
    sellerie: "",
    nombreProprietaires: "",
    optionsPrincipales: "",
    nombreCles: "",
    // Section 6: Mechanical
    fraisMecaniques: "",
    descriptionFrais: "",
    // Section 7: Condition
    accidente: "",
    descriptionAccident: "",
    defautsVisibles: "",
    janteAvantGauche: 3,
    janteAvantDroite: 3,
    janteArriereGauche: 3,
    janteArriereDroite: 3,
    // Section 8: Maintenance
    carnetComplet: "",
    // Section 9: Consent
    exactitudeInfo: false,
    accepteRgpd: false,
    signature: "",
  });

  // File states
  const [photoAvant, setPhotoAvant] = useState([]);
  const [photoArriere, setPhotoArriere] = useState([]);
  const [photoInterieur, setPhotoInterieur] = useState([]);
  const [photoCompteur, setPhotoCompteur] = useState([]);
  const [photosSupplementaires, setPhotosSupplementaires] = useState([]);
  const [carteGrise, setCarteGrise] = useState([]);
  const [justificatifsEntretien, setJustificatifsEntretien] = useState([]);

  const updateFormData = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Scroll to section
  const scrollToSection = (sectionId) => {
    sectionRefs.current[sectionId]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Track current section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;
      for (let i = sections.length; i >= 1; i--) {
        const ref = sectionRefs.current[i];
        if (ref && ref.offsetTop <= scrollPosition) {
          setCurrentSection(i);
          break;
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Validation
  const validateSection = (sectionId) => {
    switch (sectionId) {
      case 1:
        return formData.nom && formData.prenom && formData.telephone && 
               formData.email && formData.codePostal;
      case 2:
        return formData.typeDemande;
      case 3:
        return formData.marque && formData.modele && formData.paysOrigine && 
               formData.immatriculation && formData.kilometrage;
      case 4:
        return photoAvant.length > 0 && photoArriere.length > 0 && 
               photoInterieur.length > 0;
      case 5:
        return carteGrise.length > 0;
      case 6:
        return formData.fraisMecaniques && 
               (formData.fraisMecaniques === "non" || formData.descriptionFrais);
      case 7:
        return formData.accidente !== "" && 
               (formData.accidente === "non" || formData.descriptionAccident !== "");
      case 8:
        return formData.carnetComplet;
      case 9:
        return formData.exactitudeInfo && formData.accepteRgpd && formData.signature;
      default:
        return false;
    }
  };

  const progress = Math.round(
    (sections.filter((s) => validateSection(s.id)).length / sections.length) * 100
  );

  // Submit form
  const handleSubmit = async () => {
    // Validate all sections
    for (let i = 1; i <= 9; i++) {
      if (!validateSection(i)) {
        toast.error(`Veuillez compléter la section ${i}: ${sections[i-1].title}`);
        scrollToSection(i);
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // Prepare data
      const allPhotos = [
        ...photoAvant.map(f => f.path),
        ...photoArriere.map(f => f.path),
        ...photoInterieur.map(f => f.path),
        ...photoCompteur.map(f => f.path),
        ...photosSupplementaires.map(f => f.path),
      ];

      const requestData = {
        client: {
          nom: formData.nom,
          prenom: formData.prenom,
          telephone: formData.telephone,
          email: formData.email,
          adresse: "",
          code_postal: formData.codePostal,
        },
        type_demande: formData.typeDemande,
        vehicule: {
          marque: formData.marque,
          modele: formData.modele,
          date_mise_circulation: `${formData.jourCirculation}/${formData.moisCirculation}/${formData.anneeCirculation}`,
          immatriculation: formData.immatriculation,
          kilometrage: parseInt(formData.kilometrage) || 0,
          finition: formData.finition || null,
          pays_origine: formData.paysOrigine || null,
          transmission: formData.transmission || null,
          energie: formData.energie || null,
          puissance: formData.puissance || null,
          couleur: formData.couleur || null,
          sellerie: formData.sellerie || null,
          nombre_proprietaires: formData.nombreProprietaires ? parseInt(formData.nombreProprietaires) : null,
          options_principales: formData.optionsPrincipales || null,
          nombre_cles: formData.nombreCles ? parseInt(formData.nombreCles) : null,
        },
        condition: {
          accidente: formData.accidente === "oui",
          description_accident: formData.descriptionAccident || null,
          defauts_visibles: formData.defautsVisibles || null,
          jante_avant_gauche: formData.janteAvantGauche,
          jante_avant_droite: formData.janteAvantDroite,
          jante_arriere_gauche: formData.janteArriereGauche,
          jante_arriere_droite: formData.janteArriereDroite,
        },
        mecanique: {
          frais_mecaniques: formData.fraisMecaniques === "oui",
          description_frais: formData.descriptionFrais || null,
        },
        entretien: {
          carnet_complet: formData.carnetComplet === "oui",
        },
        consentement: {
          exactitude_info: formData.exactitudeInfo,
          accepte_expertise: true,
          accepte_annulation: true,
          accepte_rgpd: formData.accepteRgpd,
          signature: formData.signature,
        },
        photos: allPhotos,
        carte_grise: carteGrise.map(f => f.path),
        justificatifs_entretien: justificatifsEntretien.map(f => f.path),
      };

      // Save to database - email is sent automatically by backend via Resend
      await axios.post(`${API}/estimations`, requestData);

      setIsSubmitted(true);
      toast.success("Votre demande a été envoyée avec succès!");

    } catch (error) {
      console.error("Submit error:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
        <div className="text-center max-w-md animate-fade-in">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-white/10 flex items-center justify-center">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-semibold text-white mb-4 font-outfit">
            Demande envoyée
          </h1>
          <p className="text-white/60 mb-8">
            Merci, votre demande a bien été envoyée. Notre équipe vous recontactera dans les plus brefs délais.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-8 py-3 bg-white text-black font-medium rounded-sm hover:bg-gray-200 transition-colors"
            data-testid="new-estimation-btn"
          >
            Nouvelle estimation
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Toaster position="top-center" theme="dark" />
      
      {/* Desktop Layout */}
      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Left Sidebar - Fixed on desktop */}
        <div className="lg:fixed lg:left-0 lg:top-0 lg:w-[25%] lg:h-screen relative bg-black">
          {/* Content */}
          <div className="relative z-10 h-full flex flex-col p-8 lg:p-10">
            {/* Logo */}
            <div className="mb-8 lg:mb-12">
              <img 
                src={LOGO_URL} 
                alt="EBV Motors" 
                className="h-12 lg:h-16 w-auto"
                data-testid="logo"
              />
            </div>

            {/* Value Prop */}
            <div className="flex-1 flex flex-col justify-center">
              <h1 className="text-4xl lg:text-5xl font-light text-white mb-4 font-outfit tracking-tight">
                Estimation de votre véhicule
              </h1>
              <p className="text-white/60 text-lg mb-8 max-w-md">
                Obtenez une estimation précise pour votre véhicule en quelques minutes.
              </p>
              
              <div className="flex items-center gap-6 mb-8">
                <div className="flex items-center gap-2 text-[#0084ff]">
                  <Clock className="w-5 h-5" />
                  <span className="text-sm">3-5 minutes</span>
                </div>
                <div className="flex items-center gap-2 text-[#0084ff]">
                  <Shield className="w-5 h-5" />
                  <span className="text-sm">Données sécurisées</span>
                </div>
              </div>

              {/* Progress Indicator - Desktop */}
              <div className="hidden lg:block">
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-white/60">Progression</span>
                    <span className="text-white font-medium">{progress}%</span>
                  </div>
                  <Progress value={progress} className="h-1 bg-white/10" />
                </div>
                
                {/* Section Navigator */}
                <div className="space-y-2 mt-8">
                  {sections.map((section) => {
                    const isValid = validateSection(section.id);
                    const isCurrent = currentSection === section.id;
                    const Icon = section.icon;
                    
                    return (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-sm transition-all text-left
                          ${isCurrent ? "bg-white/10 text-white" : "text-gray-500 hover:text-white/80 hover:bg-white/5"}`}
                        data-testid={`nav-section-${section.id}`}
                      >
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs
                          ${isValid ? "bg-white text-black" : "border border-white/30"}`}>
                          {isValid ? <Check className="w-3 h-3" /> : section.id}
                        </div>
                        <Icon className="w-4 h-4" />
                        <span className="text-sm">{section.title}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="hidden lg:flex items-center gap-6 text-sm">
              <a href={`mailto:${CONTACT_EMAIL}`} className="flex items-center gap-2 text-[#0084ff] hover:text-white transition-colors">
                <Mail className="w-4 h-4" />
                {CONTACT_EMAIL}
              </a>
            </div>
          </div>
        </div>

        {/* Mobile Progress Bar */}
        <div className="lg:hidden sticky top-0 z-50 bg-[#050505] border-b border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <img src={LOGO_URL} alt="EBV Motors" className="h-8 w-auto" />
            <span className="text-white/60 text-sm">{progress}%</span>
          </div>
          <Progress value={progress} className="h-1 bg-white/10" />
        </div>

        {/* Right Content - Form */}
        <div className="lg:ml-[25%] lg:w-[75%] p-6 lg:p-12 bg-white min-h-screen">
          <div className="max-w-2xl mx-auto space-y-16">
            
            {/* Section 1: Client Info */}
            <section ref={(el) => (sectionRefs.current[1] = el)} className="scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center
                  ${validateSection(1) ? "bg-black text-white" : "border border-gray-300 text-gray-600"}`}>
                  {validateSection(1) ? <Check className="w-5 h-5" /> : "1"}
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 font-outfit">Informations Client</h2>
              </div>
              
              <div className="space-y-6 bg-gray-50 rounded-sm p-6 border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="nom" className="text-gray-700">Nom <span className="text-red-500">*</span></Label>
                    <Input
                      id="nom"
                      data-testid="input-nom"
                      value={formData.nom}
                      onChange={(e) => updateFormData("nom", e.target.value)}
                      className="bg-white border-gray-300 text-gray-900 focus:border-gray-500"
                      placeholder="Votre nom"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="prenom" className="text-gray-700">Prénom <span className="text-red-500">*</span></Label>
                    <Input
                      id="prenom"
                      data-testid="input-prenom"
                      value={formData.prenom}
                      onChange={(e) => updateFormData("prenom", e.target.value)}
                      className="bg-white border-gray-300 text-gray-900 focus:border-gray-500"
                      placeholder="Votre prénom"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="telephone" className="text-gray-700">Téléphone <span className="text-red-500">*</span></Label>
                    <Input
                      id="telephone"
                      data-testid="input-telephone"
                      type="tel"
                      value={formData.telephone}
                      onChange={(e) => updateFormData("telephone", e.target.value)}
                      className="bg-white border-gray-300 text-gray-900 focus:border-gray-500"
                      placeholder="06 XX XX XX XX"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-700">Email <span className="text-red-500">*</span></Label>
                    <Input
                      id="email"
                      data-testid="input-email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateFormData("email", e.target.value)}
                      className="bg-white border-gray-300 text-gray-900 focus:border-gray-500"
                      placeholder="votre@email.com"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="codePostal" className="text-gray-700">Code postal <span className="text-red-500">*</span></Label>
                  <Input
                    id="codePostal"
                    data-testid="input-code-postal"
                    value={formData.codePostal}
                    onChange={(e) => updateFormData("codePostal", e.target.value)}
                    className="bg-white border-gray-300 text-gray-900 focus:border-gray-500 max-w-[200px]"
                    placeholder="75000"
                  />
                </div>
              </div>
            </section>

            {/* Section 2: Request Type */}
            <section ref={(el) => (sectionRefs.current[2] = el)} className="scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center
                  ${validateSection(2) ? "bg-black text-white" : "border border-gray-300 text-gray-600"}`}>
                  {validateSection(2) ? <Check className="w-5 h-5" /> : "2"}
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 font-outfit">Votre Demande</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {["Reprise", "Rachat cash", "Dépôt-vente"].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => updateFormData("typeDemande", type)}
                    className={`selection-card p-6 rounded-sm border text-center transition-all
                      ${formData.typeDemande === type 
                        ? "border-black bg-gray-100" 
                        : "border-gray-200 hover:border-gray-400"}`}
                    data-testid={`type-${type.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <span className="text-gray-900 font-medium">{type}</span>
                  </button>
                ))}
              </div>
            </section>

            {/* Section 3: Vehicle Info */}
            <section ref={(el) => (sectionRefs.current[3] = el)} className="scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center
                  ${validateSection(3) ? "bg-black text-white" : "border border-gray-300 text-gray-600"}`}>
                  {validateSection(3) ? <Check className="w-5 h-5" /> : "3"}
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 font-outfit">Informations Véhicule</h2>
              </div>
              
              <div className="space-y-6 bg-gray-50 rounded-sm p-6 border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Marque <span className="text-red-500">*</span></Label>
                    <Input
                      data-testid="input-marque"
                      value={formData.marque}
                      onChange={(e) => updateFormData("marque", e.target.value)}
                      className="bg-white border-gray-300 text-gray-900 focus:border-gray-500"
                      placeholder="Ex: Porsche"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Modèle <span className="text-red-500">*</span></Label>
                    <Input
                      data-testid="input-modele"
                      value={formData.modele}
                      onChange={(e) => updateFormData("modele", e.target.value)}
                      className="bg-white border-gray-300 text-gray-900 focus:border-gray-500"
                      placeholder="Ex: 911 Carrera"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Immatriculation <span className="text-red-500">*</span></Label>
                    <Input
                      data-testid="input-immatriculation"
                      value={formData.immatriculation}
                      onChange={(e) => updateFormData("immatriculation", e.target.value.toUpperCase())}
                      className="bg-white border-gray-300 text-gray-900 focus:border-gray-500 uppercase"
                      placeholder="AA-123-BB"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Kilométrage <span className="text-red-500">*</span></Label>
                    <Input
                      data-testid="input-kilometrage"
                      type="number"
                      value={formData.kilometrage}
                      onChange={(e) => updateFormData("kilometrage", e.target.value)}
                      className="bg-white border-gray-300 text-gray-900 focus:border-gray-500"
                      placeholder="50000"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-700">Pays d'origine <span className="text-red-500">*</span></Label>
                  <Input
                    data-testid="input-pays"
                    value={formData.paysOrigine}
                    onChange={(e) => updateFormData("paysOrigine", e.target.value)}
                    className="bg-white border-gray-300 text-gray-900 focus:border-gray-500"
                    placeholder="Ex: Allemagne"
                  />
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <p className="text-gray-500 text-sm mb-4">Champs facultatifs</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-gray-700">Finition</Label>
                      <Input
                        data-testid="input-finition"
                        value={formData.finition}
                        onChange={(e) => updateFormData("finition", e.target.value)}
                        className="bg-white border-gray-300 text-gray-900 focus:border-gray-500"
                        placeholder="Ex: GT3 RS"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Couleur</Label>
                      <Input
                        data-testid="input-couleur"
                        value={formData.couleur}
                        onChange={(e) => updateFormData("couleur", e.target.value)}
                        className="bg-white border-gray-300 text-gray-900 focus:border-gray-500"
                        placeholder="Ex: Noir"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    <div className="space-y-2">
                      <Label className="text-gray-700">Sellerie</Label>
                      <Input
                        data-testid="input-sellerie"
                        value={formData.sellerie}
                        onChange={(e) => updateFormData("sellerie", e.target.value)}
                        className="bg-white border-gray-300 text-gray-900 focus:border-gray-500"
                        placeholder="Ex: Cuir noir"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Nombre de propriétaires</Label>
                      <Input
                        data-testid="input-proprietaires"
                        type="number"
                        value={formData.nombreProprietaires}
                        onChange={(e) => updateFormData("nombreProprietaires", e.target.value)}
                        className="bg-white border-gray-300 text-gray-900 focus:border-gray-500"
                        placeholder="1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2 mt-6">
                    <Label className="text-gray-700">Options principales</Label>
                    <Textarea
                      data-testid="input-options"
                      value={formData.optionsPrincipales}
                      onChange={(e) => updateFormData("optionsPrincipales", e.target.value)}
                      className="bg-white border-gray-300 text-gray-900 focus:border-gray-500 min-h-[100px]"
                      placeholder="Liste des options principales du véhicule..."
                    />
                  </div>

                  <div className="space-y-2 mt-6">
                    <Label className="text-gray-700">Nombre de clés disponibles</Label>
                    <div className="flex gap-4">
                      {["1", "2"].map((value) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => updateFormData("nombreCles", value)}
                          className={`px-8 py-3 rounded-sm border transition-all
                            ${formData.nombreCles === value 
                              ? "border-black bg-gray-100 text-gray-900" 
                              : "border-gray-200 text-gray-600 hover:border-gray-400"}`}
                          data-testid={`cles-${value}`}
                        >
                          {value}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 4: Vehicle Photos */}
            <section ref={(el) => (sectionRefs.current[4] = el)} className="scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center
                  ${validateSection(4) ? "bg-black text-white" : "border border-gray-300 text-gray-600"}`}>
                  {validateSection(4) ? <Check className="w-5 h-5" /> : "4"}
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 font-outfit">Photos du Véhicule</h2>
              </div>
              
              <div className="space-y-6 bg-gray-50 rounded-sm p-6 border border-gray-200">
                <p className="text-gray-500 text-sm">Photos obligatoires</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FileUpload
                    label="Photo 3/4 avant droit"
                    required
                    accept=".jpg,.jpeg,.png,.heic,.webp"
                    files={photoAvant}
                    onFilesChange={setPhotoAvant}
                    testId="upload-avant"
                  />
                  <FileUpload
                    label="Photo 3/4 arrière gauche"
                    required
                    accept=".jpg,.jpeg,.png,.heic,.webp"
                    files={photoArriere}
                    onFilesChange={setPhotoArriere}
                    testId="upload-arriere"
                  />
                  <FileUpload
                    label="Photo intérieur habitacle"
                    required
                    accept=".jpg,.jpeg,.png,.heic,.webp"
                    files={photoInterieur}
                    onFilesChange={setPhotoInterieur}
                    testId="upload-interieur"
                  />
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <p className="text-gray-500 text-sm mb-4">Photos supplémentaires (facultatif)</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FileUpload
                      label="Photo compteur (moteur allumé)"
                      accept=".jpg,.jpeg,.png,.heic,.webp"
                      files={photoCompteur}
                      onFilesChange={setPhotoCompteur}
                      testId="upload-compteur"
                    />
                    <FileUpload
                      label="Défauts, options, détails"
                      multiple
                      accept=".jpg,.jpeg,.png,.heic,.webp"
                      files={photosSupplementaires}
                      onFilesChange={setPhotosSupplementaires}
                      testId="upload-supplementaires"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Section 5: Carte Grise */}
            <section ref={(el) => (sectionRefs.current[5] = el)} className="scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center
                  ${validateSection(5) ? "bg-black text-white" : "border border-gray-300 text-gray-600"}`}>
                  {validateSection(5) ? <Check className="w-5 h-5" /> : "5"}
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 font-outfit">Carte Grise</h2>
              </div>
              
              <div className="bg-gray-50 rounded-sm p-6 border border-gray-200">
                <FileUpload
                  label="Carte grise (recto)"
                  required
                  accept=".jpg,.jpeg,.png,.pdf,.heic,.webp"
                  files={carteGrise}
                  onFilesChange={setCarteGrise}
                  testId="upload-carte-grise"
                />
              </div>
            </section>

            {/* Section 6: Mechanical */}
            <section ref={(el) => (sectionRefs.current[6] = el)} className="scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center
                  ${validateSection(6) ? "bg-black text-white" : "border border-gray-300 text-gray-600"}`}>
                  {validateSection(6) ? <Check className="w-5 h-5" /> : "6"}
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 font-outfit">Frais de remise en état</h2>
              </div>
              
              <div className="space-y-6 bg-gray-50 rounded-sm p-6 border border-gray-200">
                <div className="space-y-2">
                  <Label className="text-gray-700">Des frais de remise en état sont-ils à prévoir ? <span className="text-red-500">*</span></Label>
                  <div className="flex gap-4">
                    {["oui", "non"].map((value) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => updateFormData("fraisMecaniques", value)}
                        className={`px-6 py-3 rounded-sm border transition-all capitalize
                          ${formData.fraisMecaniques === value 
                            ? "border-black bg-gray-100 text-gray-900" 
                            : "border-gray-200 text-gray-600 hover:border-gray-400"}`}
                        data-testid={`frais-${value}`}
                      >
                        {value === "oui" ? "Oui" : "Non"}
                      </button>
                    ))}
                  </div>
                </div>

                {formData.fraisMecaniques === "oui" && (
                  <div className="space-y-2 animate-fade-in">
                    <Label className="text-gray-700">Description des frais <span className="text-red-500">*</span></Label>
                    <Textarea
                      data-testid="input-description-frais"
                      value={formData.descriptionFrais}
                      onChange={(e) => updateFormData("descriptionFrais", e.target.value)}
                      className="bg-white border-gray-300 text-gray-900 focus:border-gray-500 min-h-[100px]"
                      placeholder="Décrivez les frais de remise en état à prévoir..."
                    />
                  </div>
                )}
              </div>
            </section>

            {/* Section 7: Condition */}
            <section ref={(el) => (sectionRefs.current[7] = el)} className="scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center
                  ${validateSection(7) ? "bg-black text-white" : "border border-gray-300 text-gray-600"}`}>
                  {validateSection(7) ? <Check className="w-5 h-5" /> : "7"}
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 font-outfit">Défauts de carrosserie</h2>
              </div>
              
              <div className="space-y-6 bg-gray-50 rounded-sm p-6 border border-gray-200">
                <div className="space-y-2">
                  <Label className="text-gray-700">Véhicule accidenté ? <span className="text-red-500">*</span></Label>
                  <div className="flex gap-4">
                    {["oui", "non"].map((value) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => updateFormData("accidente", value)}
                        className={`px-6 py-3 rounded-sm border transition-all capitalize
                          ${formData.accidente === value 
                            ? "border-black bg-gray-100 text-gray-900" 
                            : "border-gray-200 text-gray-600 hover:border-gray-400"}`}
                        data-testid={`accidente-${value}`}
                      >
                        {value === "oui" ? "Oui" : "Non"}
                      </button>
                    ))}
                  </div>
                </div>

                {formData.accidente === "oui" && (
                  <div className="space-y-2 animate-fade-in">
                    <Label className="text-gray-700">Description de l'accident <span className="text-red-500">*</span></Label>
                    <Textarea
                      data-testid="input-description-accident"
                      value={formData.descriptionAccident}
                      onChange={(e) => updateFormData("descriptionAccident", e.target.value)}
                      className="bg-white border-gray-300 text-gray-900 focus:border-gray-500 min-h-[100px]"
                      placeholder="Décrivez l'accident..."
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-gray-700">Défauts ou dommages visibles</Label>
                  <Textarea
                    data-testid="input-defauts"
                    value={formData.defautsVisibles}
                    onChange={(e) => updateFormData("defautsVisibles", e.target.value)}
                    className="bg-white border-gray-300 text-gray-900 focus:border-gray-500 min-h-[100px]"
                    placeholder="Rayures, impacts, défauts de carrosserie..."
                  />
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-start gap-4 mb-6">
                    <img 
                      src={WHEEL_IMAGE} 
                      alt="Jante" 
                      className="w-24 h-24 object-cover rounded-sm opacity-60"
                    />
                    <div>
                      <h3 className="text-gray-900 font-medium mb-1">État des jantes</h3>
                      <p className="text-gray-500 text-sm">Évaluez l'état de chaque jante de 1 à 5 étoiles</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <StarRating
                      label="Jante avant gauche"
                      value={formData.janteAvantGauche}
                      onChange={(v) => updateFormData("janteAvantGauche", v)}
                    />
                    <StarRating
                      label="Jante avant droite"
                      value={formData.janteAvantDroite}
                      onChange={(v) => updateFormData("janteAvantDroite", v)}
                    />
                    <StarRating
                      label="Jante arrière gauche"
                      value={formData.janteArriereGauche}
                      onChange={(v) => updateFormData("janteArriereGauche", v)}
                    />
                    <StarRating
                      label="Jante arrière droite"
                      value={formData.janteArriereDroite}
                      onChange={(v) => updateFormData("janteArriereDroite", v)}
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Section 8: Maintenance */}
            <section ref={(el) => (sectionRefs.current[8] = el)} className="scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center
                  ${validateSection(8) ? "bg-black text-white" : "border border-gray-300 text-gray-600"}`}>
                  {validateSection(8) ? <Check className="w-5 h-5" /> : "8"}
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 font-outfit">Entretien</h2>
              </div>
              
              <div className="space-y-6 bg-gray-50 rounded-sm p-6 border border-gray-200">
                <div className="space-y-2">
                  <Label className="text-gray-700">Carnet d'entretien complet ? <span className="text-red-500">*</span></Label>
                  <div className="flex gap-4">
                    {["oui", "non"].map((value) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => updateFormData("carnetComplet", value)}
                        className={`px-6 py-3 rounded-sm border transition-all capitalize
                          ${formData.carnetComplet === value 
                            ? "border-black bg-gray-100 text-gray-900" 
                            : "border-gray-200 text-gray-600 hover:border-gray-400"}`}
                        data-testid={`carnet-${value}`}
                      >
                        {value === "oui" ? "Oui" : "Non"}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <p className="text-gray-500 text-sm mb-4">Justificatifs d'entretien (facultatif)</p>
                  <FileUpload
                    label="Factures, carnet d'entretien, etc."
                    multiple
                    accept=".jpg,.jpeg,.png,.pdf,.heic,.webp"
                    files={justificatifsEntretien}
                    onFilesChange={setJustificatifsEntretien}
                    testId="upload-justificatifs"
                  />
                </div>
              </div>
            </section>

            {/* Section 9: Consent */}
            <section ref={(el) => (sectionRefs.current[9] = el)} className="scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center
                  ${validateSection(9) ? "bg-black text-white" : "border border-gray-300 text-gray-600"}`}>
                  {validateSection(9) ? <Check className="w-5 h-5" /> : "9"}
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 font-outfit">Déclaration & Consentement</h2>
              </div>
              
              <div className="space-y-6 bg-gray-50 rounded-sm p-6 border border-gray-200">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="exactitude"
                      data-testid="checkbox-exactitude"
                      checked={formData.exactitudeInfo}
                      onCheckedChange={(checked) => updateFormData("exactitudeInfo", checked)}
                      className="mt-1 border-gray-300 data-[state=checked]:bg-black data-[state=checked]:text-white"
                    />
                    <Label htmlFor="exactitude" className="text-gray-700 cursor-pointer">
                      Je certifie l'exactitude des informations fournies <span className="text-red-500">*</span>
                    </Label>
                  </div>

                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="rgpd"
                      data-testid="checkbox-rgpd"
                      checked={formData.accepteRgpd}
                      onCheckedChange={(checked) => updateFormData("accepteRgpd", checked)}
                      className="mt-1 border-gray-300 data-[state=checked]:bg-black data-[state=checked]:text-white"
                    />
                    <Label htmlFor="rgpd" className="text-gray-700 cursor-pointer">
                      J'accepte la politique de confidentialité et le traitement de mes données personnelles conformément au RGPD <span className="text-red-500">*</span>
                    </Label>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <div className="space-y-2">
                    <Label className="text-gray-700">
                      Signature <span className="text-red-500">*</span>
                    </Label>
                    <p className="text-gray-500 text-sm mb-2">
                      Veuillez recopier votre nom complet en lettres majuscules
                    </p>
                    <Input
                      data-testid="input-signature"
                      value={formData.signature}
                      onChange={(e) => updateFormData("signature", e.target.value.toUpperCase())}
                      className="bg-white border-gray-300 text-gray-900 focus:border-gray-500 uppercase text-lg tracking-wider"
                      placeholder="VOTRE NOM COMPLET"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Submit Button */}
            <div className="pt-8 pb-16">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting || progress < 100}
                className={`w-full py-4 rounded-sm font-medium text-lg transition-all flex items-center justify-center gap-3
                  ${progress === 100 
                    ? "bg-black text-white hover:bg-gray-800" 
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"}`}
                data-testid="submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    Obtenir mon estimation
                    <ChevronRight className="w-5 h-5" />
                  </>
                )}
              </button>
              
              {progress < 100 && (
                <p className="text-gray-400 text-sm text-center mt-4">
                  Complétez toutes les sections obligatoires pour soumettre votre demande
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
